// src/router/routes.js
// 라우트 정의 + SEO 상수 분리
// ✅ 2026.01.31 - SEO 메타 데이터 보강 (title 10자+, description 50자+)

import MyPageLayout from '@/views/mypage/MyPageLayout.vue';

export const ROLES = {
  USER: 'ROLE_USER',
  ADMIN: 'ROLE_ADMIN',
  VENDOR: 'ROLE_VENDOR',
  VIP: 'ROLE_VIP',
};

export const DEFAULT_HOME_ROUTE = 'home';
export const GUEST_ONLY_PAGES = new Set(['login', 'signup', 'forgotPassword']);

// ✅ 수정: 브랜드 suffix 통일 (공백 제거)
export const BRAND_NAME = '엠피니티';
export const BRAND_SUFFIX = ' | 엠피니티(MPNITE)';

// ✅ 수정: DEFAULT_DESCRIPTION도 홈 전용으로 변경
export const DEFAULT_DESCRIPTION =
  '엠피니티는 시제품부터 양산까지 원스톱으로 의뢰할 수 있는 온라인 제조 플랫폼입니다. 다양한 제조 방식을 한 곳에서 비교하세요.';

export const SEO_BASE_URL = 'https://mpnite.com';
export const DEFAULT_OG_IMAGE = 'https://mpnite.com/og-image.png';

const HOME_BC = [{ name: "엠피니티", url: "/" }];
//                        ↑ ✅ "홈" → "엠피니티" (네이버 가이드: 서술적 이름)
const bc = (...items) => items;


// ============================================================================
// 라우트 정의
// ✅ title: 전체 제목 포함 (10자 이상)
// ✅ description: 50자 이상 160자 이하
// ============================================================================

export const routes = [
  // ──────────────────────────────────────────────────────────────────────────
  // 1) 홈/메인 (공개)
  // ──────────────────────────────────────────────────────────────────────────
 // ──────────────────────────────────────────────────────────────────
  // 1) 홈 — 대표 키워드: "엠피니티", "온라인 제조 플랫폼"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/IndexView.vue'),
    meta: {
      // ❌ 기존: '쉽고 빠른 3D프린팅 출력·제작 플랫폼 | 엠피니티(MPNITE)'
      // ✅ 수정: 브랜드 선두, "3D프린팅" 제거
      title: '엠피니티(MPNITE) | 온라인 제조 플랫폼',

      // ❌ 기존: 'AI 자동견적으로 3D프린팅, CNC가공...'
      // ✅ 수정: 포괄적 표현, 특정 서비스 키워드 제거
      description: '엠피니티는 시제품부터 양산까지 원스톱으로 의뢰할 수 있는 온라인 제조 플랫폼입니다. 다양한 제조 방식을 한 곳에서 비교하세요.',

      schemaKind: "home",
      breadcrumb: HOME_BC,
      sitemap: { priority: 1.0, changefreq: 'daily' },
    },
  },


  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginPage.vue"),
    meta: {
      title: '로그인 | 엠피니티(MPNITE)',
      description: '엠피니티 로그인 페이지입니다. 3D프린팅 견적 및 주문 서비스를 이용하시려면 로그인해주세요.',
      noIndex: true,
      sitemap: false,
    },
  },


  // ──────────────────────────────────────────────────────────────────
  // 2) 회사 소개 — 대표 키워드: "회사 소개"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutMain.vue'),
    meta: {
      // ❌ 기존: '회사 소개 | 엠피니티(MPNITE)'  → OK이지만 desc가 문제
      title: '회사 소개 | 엠피니티(MPNITE)',

      // ❌ 기존: 'AI 자동견적 3D프린팅 플랫폼 엠피니티를 소개합니다...'
      //    → "AI 자동견적", "3D프린팅" 모두 포함 = 3개 페이지와 경합!
      // ✅ 수정: 회사 역할 중심, 서비스 키워드 제거
      description: '제조 수행사와 발주사를 연결하는 B2B 온라인 제조 플랫폼의 역할과 주요 기능을 소개합니다.',

      schemaKind: "about",
      breadcrumb: bc(...HOME_BC, { name: "회사 소개" }),
      sitemap: { priority: 0.8, changefreq: 'monthly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 3) 소재 정보 — 대표 키워드: "3D프린팅 소재", "재료 정보"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/materialinfo',
    name: 'materialinfo',
    component: () => import('@/views/MaterialInfo.vue'),
    meta: {
      // ✅ 기존 유지 (고유한 키워드)
      title: '3D프린팅 소재 정보 | 엠피니티(MPNITE)',

      // ✅ 기존 유지 (소재 특화)
      description: 'FDM, SLA, SLS 3D프린터 소재 안내. PLA, ABS, 레진, 나일론(PA12) 등 다양한 재료의 특성, 용도, 가격 정보를 확인하세요.',

      schemaKind: "info",
      breadcrumb: bc(...HOME_BC, { name: "소재 정보" }),
      sitemap: { priority: 0.8, changefreq: 'weekly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 4) 서비스 안내 — 대표 키워드: "서비스 이용 방법", "이용 안내"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/serviceinfo',
    name: 'serviceinfo',
    component: () => import('@/views/ServiceInfo.vue'),
    meta: {
      title: '서비스 안내 | 엠피니티(MPNITE)',

      // ❌ 기존: '엠피니티 3D프린팅 서비스 이용 방법을 안내합니다. AI 자동 견적부터...'
      //    → "엠피니티" 선두 + "3D프린팅" + "AI 자동 견적" = 3중 경합
      // ✅ 수정: "이용 방법" 중심, 다른 키워드 최소화
      description: '온라인 제조 플랫폼 이용 방법을 안내합니다. 파일 업로드부터 견적 확인, 제조, 품질검수, 배송까지 전 과정을 확인하세요.',

      schemaKind: "info",
      breadcrumb: bc(...HOME_BC, { name: "서비스 안내" }),
      sitemap: { priority: 0.8, changefreq: 'monthly' },
    },
  },
  // ──────────────────────────────────────────────────────────────────
  // 5) 장비 안내 — 대표 키워드: "3D프린터 장비"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/machine',
    name: 'machine',
    component: () => import('@/views/MachineView.vue'),
    meta: {
      // ✅ 유지 (고유)
      title: '3D프린터 장비 안내 | 엠피니티(MPNITE)',
      description: 'FDM, SLA, SLS, MJF 3D프린터 및 CNC, 판금 장비 정보를 제공합니다. 장비별 특징과 출력 가능 사이즈를 확인하세요.',
      schemaKind: "info",
      breadcrumb: bc(...HOME_BC, { name: "장비 안내" }),
      sitemap: { priority: 0.7, changefreq: 'monthly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 6) 지도 — 대표 키워드: "제조 파트너 위치"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/map',
    name: 'map',
    component: () => import('@/views/MapView.vue'),
    meta: {
      title: '제조 파트너 위치 | 엠피니티(MPNITE)',

      // ❌ 기존: '엠피니티 3D프린팅 제조 파트너사 위치를...'
      // ✅ 수정: "엠피니티" + "3D프린팅" 제거
      description: '제조 파트너사 위치를 지도에서 확인하세요. 전국 네트워크로 빠른 제조 서비스를 제공합니다.',

      schemaKind: "info",
      breadcrumb: bc(...HOME_BC, { name: "파트너 지도" }),
      sitemap: { priority: 0.5, changefreq: 'monthly' },
    },
  },
  // ──────────────────────────────────────────────────────────────────────────
  // 3) 제조 서비스 페이지 (공개 - 핵심 SEO 페이지)
  // ──────────────────────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────────
  // 7) 3D프린팅 서비스 — 대표 키워드: "3D프린팅 제조 서비스"
  //    ⭐ 이 페이지가 "3D프린팅" 검색의 대표!
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/3dmanufacturing',
    name: '3dmanufacturing',
    component: () => import('@/views/manufacturing/3dManufacturing.vue'),
    meta: {
      // ✅ 유지 (이 페이지가 "3D프린팅 서비스"의 대표)
      title: '3D프린팅 제조 서비스 | 엠피니티(MPNITE)',

      // ✅ 미세 수정: "AI 자동견적" 제거 → /quotestart 전담
      description: 'FDM, SLA, SLS, MJF 3D프린터로 시제품을 제작하세요. 도면 업로드만으로 빠르고 정확한 3D프린팅 출력 서비스를 이용할 수 있습니다.',

      schemaKind: "service",
      breadcrumb: bc(...HOME_BC, { name: "3D프린팅 서비스" }),
      sitemap: { priority: 0.9, changefreq: 'weekly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 8) 3D프린팅 공정 상세
  // ──────────────────────────────────────────────────────────────────
  {
    path: "/3dmanufacturing/process/:processKey(fdm|sla|dlp|sls|mjf|slm|ebm|diw|inkjet)",
    name: "3dProcessDetail",
    component: () => import("@/views/manufacturing/3dProcessDetail.vue"),
    meta: {
      // ✅ 유지 (동적 페이지, processKey로 분화)
      title: '3D프린팅 공정 상세 | 엠피니티(MPNITE)',
      description: 'FDM, SLA, SLS 등 3D프린팅 공정별 특징과 적용 분야를 안내합니다. 프로젝트에 최적의 공정을 선택하세요.',
      schemaKind: "processArticle",
      breadcrumb: bc(
        ...HOME_BC,
        { name: "3D프린팅 서비스", url: "/3dmanufacturing" },
        { name: "공정 상세" }
      ),
      sitemap: { priority: 0.7, changefreq: "monthly" },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 9) 사출성형·주조 — 대표 키워드: "사출성형", "주조"
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/castingmoldingmanufacturing',
    name: 'castingmoldingmanufacturing',
    component: () => import('@/views/manufacturing/castingMoldingManufacturing.vue'),
    meta: {
      title: '사출성형·주조 제조 서비스 | 엠피니티(MPNITE)',

      // ❌ 기존: '...소량 시제품부터 양산까지 AI 자동견적으로...'
      // ✅ 수정: "AI 자동견적" 제거, "시제품~양산"은 이 서비스 특성으로 유지
      description: '플라스틱 사출, 실리콘 주형, 다이캐스팅 제조 서비스. 소량 시제품부터 양산 금형까지 온라인으로 견적을 확인하세요.',

      schemaKind: "service",
      breadcrumb: bc(...HOME_BC, { name: "사출·주조 서비스" }),
      sitemap: { priority: 0.9, changefreq: 'weekly' },
    },
  },
  // ──────────────────────────────────────────────────────────────────
  // 10) CNC 가공 — 대표 키워드: "CNC 정밀 가공" ✅ 경합 없음
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/cncmanufacturing',
    name: 'cncmanufacturing',
    component: () => import('@/views/manufacturing/cncManufacturing.vue'),
    meta: {
      title: 'CNC 정밀 가공 서비스 | 엠피니티(MPNITE)',
      description: 'CNC 밀링, 선반, 5축 정밀 가공 서비스. 알루미늄, 스테인리스, 플라스틱 등 다양한 소재의 절삭 가공을 제공합니다.',
      schemaKind: "service",
      breadcrumb: bc(...HOME_BC, { name: "CNC 가공 서비스" }),
      sitemap: { priority: 0.9, changefreq: 'weekly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 11) 판금 가공 — 대표 키워드: "판금 가공" ✅ 경합 없음
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/sheetmetalmanufacturing',
    name: 'sheetmetalmanufacturing',
    component: () => import('@/views/manufacturing/sheetMetalManufacturing.vue'),
    meta: {
      title: '판금 가공 제조 서비스 | 엠피니티(MPNITE)',
      description: '레이저 커팅, 절곡, 용접, 표면처리 원스톱 판금 서비스. 인클로저, 브라켓, 패널 등 금속 제품을 제작하세요.',
      schemaKind: "service",
      breadcrumb: bc(...HOME_BC, { name: "판금 가공 서비스" }),
      sitemap: { priority: 0.9, changefreq: 'weekly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 12) 장비 상세
  // ──────────────────────────────────────────────────────────────────
  {
    path: "/equipment/:processKey(fdm|sla|dlp|sls|slm)",
    name: "EquipmentDetail",
    component: () => import("@/views/EquipmentDetail.vue"),
    meta: {
      title: '장비 라인업 상세 | 엠피니티(MPNITE)',
      description: 'FDM, SLA, SLS, SLM 공정별 운영 장비와 지원 범위, 품질 기준을 안내합니다. 프로젝트에 적합한 장비를 확인하세요.',
      schemaKind: "equipment",
      breadcrumb: bc(
        ...HOME_BC,
        { name: "서비스 안내", url: "/serviceinfo" },
        { name: "장비 라인업" }
      ),
      sitemap: { priority: 0.7, changefreq: "monthly" },
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 4) 견적 관련 (공개 - 핵심 SEO 페이지)
  // ──────────────────────────────────────────────────────────────────────────

  // ──────────────────────────────────────────────────────────────────
  // 13) AI 자동견적 — 대표 키워드: "AI 자동견적"
  //     ⭐ 이 페이지가 "AI 자동견적" 검색의 유일한 대표!
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/quotestart',
    name: 'quotestart',
    component: () => import('@/views/ideaquote/QuoteStart.vue'),
    meta: {
      title: 'AI 자동견적 시작하기 | 엠피니티(MPNITE)',

      // ❌ 기존: '...STL, STEP 파일로 쉽고 빠르게 3D프린팅 견적을...'
      //    → "3D프린팅 견적" = /3dmanufacturing과 경합
      // ✅ 수정: "AI 자동견적" 기능 설명에 집중
      description: '3D 파일을 업로드하면 AI가 즉시 제조 견적을 제공합니다. STL, STEP 등 다양한 파일 포맷을 지원합니다.',

      schemaKind: "softwareApp",
      breadcrumb: bc(...HOME_BC, { name: "AI 자동견적" }),
      sitemap: { priority: 0.9, changefreq: 'daily' },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 14) 아이디어 견적 — 대표 키워드: "아이디어 견적" ✅ 고유
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/quotenomodel',
    name: 'quotenomodel',
    component: () => import('@/views/ideaquote/QuoteNoModelForm.vue'),
    meta: {
      title: '아이디어 견적 요청 | 엠피니티(MPNITE)',
      description: '3D 모델이 없어도 아이디어만으로 시제품 제작 견적을 요청할 수 있습니다. 설명과 참고자료로 맞춤 견적을 받아보세요.',
      schemaKind: "service",
      breadcrumb: bc(...HOME_BC, { name: "아이디어 견적" }),
      sitemap: { priority: 0.8, changefreq: 'weekly' },
    },
  },
  {
    path: '/quoteformcomplete',
    name: 'quoteformcomplete',
    component: () => import('@/views/ideaquote/QuoteFormComplete.vue'),
    meta: {
      title: '견적 요청 완료 | 엠피니티(MPNITE)',
      description: '견적 요청이 완료되었습니다. 담당자가 검토 후 빠른 시일 내에 연락드리겠습니다.',
      noIndex: true,
      sitemap: false,
    },
  },
  // ──────────────────────────────────────────────────────────────────
  // 15) 도면 업로드 견적
  // ──────────────────────────────────────────────────────────────────
  {
    path: "/quoteafterupload",
    name: "quoteafterupload",
    component: () => import("@/views/QuoteAfterUpload.vue"),
    meta: {
      title: '도면 업로드 견적 | 엠피니티(MPNITE)',
      description: 'STL, STEP 도면 파일을 업로드하고 맞춤 제조 견적을 빠르게 요청하세요. 다양한 파일 포맷을 지원합니다.',
      breadcrumb: bc(...HOME_BC, { name: "도면 업로드 견적" }),
      sitemap: { priority: 0.8, changefreq: "weekly" },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 16) 소재 선택
  // ──────────────────────────────────────────────────────────────────
  {
    path: "/quotematerial",
    name: "quotematerial",
    component: () => import("@/views/scoreboard/MaterialSelection.vue"),
    meta: {
      title: '제조 소재 선택 | 엠피니티(MPNITE)',
      description: '프로젝트 요구사항에 맞는 최적의 소재를 선택하세요. PLA, ABS, 레진, 나일론, 금속 등 다양한 재료를 비교할 수 있습니다.',
      breadcrumb: bc(...HOME_BC, { name: "소재 선택" }),
      sitemap: { priority: 0.7, changefreq: "weekly" },
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // 17) 가공 옵션
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/quoteoption',
    name: 'quoteoption',
    component: () => import('@/views/quote/QuoteFileOptionScreen.vue'),
    meta: {
      title: '가공 옵션 선택 | 엠피니티(MPNITE)',

      // ❌ 기존: '3D프린팅 후가공, 도색...'
      // ✅ 수정: "3D프린팅" 제거 (가공 옵션은 모든 공정에 해당)
      description: '후가공, 도색, 조립 등 가공 옵션을 선택하세요. 완성도 높은 제품을 위한 다양한 마감 서비스를 제공합니다.',

      breadcrumb: bc(...HOME_BC, { name: "가공 옵션" }),
      sitemap: { priority: 0.5, changefreq: 'monthly' },
    },
  },
  // ──────────────────────────────────────────────────────────────────
  // 18) 장비 선택 (견적 프로세스)
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/quotemachine',
    name: 'quotemachine',
    component: () => import('@/views/machine/QuoteMachine.vue'),
    meta: {
      title: '제조 장비 선택 | 엠피니티(MPNITE)',
      // ✅ 수정: "견적 비교" 기능에 집중
      description: '프로젝트에 가장 적합한 장비와 협력사를 선택하세요. 품질, 가격, 납기를 한눈에 비교할 수 있습니다.',
      breadcrumb: bc(...HOME_BC, { name: "장비 선택" }),
      sitemap: { priority: 0.6, changefreq: "weekly" },
    },
  },

  {
    path: '/quotecomplete',
    name: 'quotecomplete',
    component: () => import('@/views/QuoteComplete.vue'),
    meta: {
      title: '견적 완료 | 엠피니티(MPNITE)',
      description: '견적이 완료되었습니다. 주문을 진행하시거나 추가 옵션을 선택하실 수 있습니다.',
      noIndex: true,
      sitemap: false,
    },
  },
 // ──────────────────────────────────────────────────────────────────
  // 19) 견적 마법사
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/estimatewizard',
    name: 'estimatewizard',
    component: () => import('@/views/estimate/EstimateWizard.vue'),
    meta: {
      title: '견적 마법사 | 엠피니티(MPNITE)',

      // ❌ 기존: '...쉽게 3D프린팅, CNC, 판금, 사출 제조 견적을...'
      //    → 4개 서비스 키워드 나열 = 모든 서비스 페이지와 경합
      // ✅ 수정: "단계별 가이드" 기능에 집중
      description: '단계별 가이드를 따라 쉽게 제조 견적을 받아보세요. 초보자도 간편하게 이용할 수 있는 견적 도우미입니다.',

      schemaKind: "softwareApp",
      breadcrumb: bc(...HOME_BC, { name: "견적 마법사" }),
      sitemap: { priority: 0.8, changefreq: 'weekly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 5) 결제 관련 (로그인 필요 - sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: "/quotecheckout/:orderId",
    name: "quotecheckout",
    component: () => import("@/views/QuoteCheckout.vue"),
    meta: {
      requiresAuth: true,
      title: '주문 결제 | 엠피니티(MPNITE)',
      description: '견적 확인 후 안전하게 결제를 진행하세요. 다양한 결제 수단을 지원합니다.',
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/quotecheckoutfailed',
    name: 'quotecheckoutfailed',
    component: () => import('@/views/QuoteCheckoutFailed.vue'),
    meta: {
      title: '결제 실패 | 엠피니티(MPNITE)',
      description: '결제 처리 중 문제가 발생했습니다. 다시 시도하시거나 고객센터에 문의해주세요.',
      noIndex: true,
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 6) 장바구니 (로그인 필요 - sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/cartview',
    name: 'cartview',
    component: () => import('@/views/CartView.vue'),
    meta: {
      requiresAuth: true,
      title: '장바구니 | 엠피니티(MPNITE)',
      description: '장바구니에 담긴 제품을 확인하고 주문을 진행하세요.',
      noIndex: true,
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 7) 파트너 문의 (공개)
  // ──────────────────────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────────
  // 20) 파트너 모집
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/partner/inquiry',
    name: 'PartnerInquiry',
    component: () => import('@/views/recruitment/PartnerInquiry.vue'),
    alias: ['/partnerinquiry'],
    meta: {
      title: '제조 파트너 모집 | 엠피니티(MPNITE)',

      // ❌ 기존: '3D프린터, CNC 장비를 보유한...'
      // ✅ 수정: "파트너 모집" 중심, 구체적 장비명 제거
      description: '제조 장비를 보유한 기업 파트너를 모집합니다. 새로운 발주 기회를 만들어보세요.',

      schemaKind: "partnerInquiry",
      breadcrumb: bc(...HOME_BC, { name: "파트너 모집" }),
      sitemap: { priority: 0.7, changefreq: 'monthly' },
    },
  },
  {
    path: '/partner/inquiry/complete',
    name: 'PartnerInquiryComplete',
    component: () => import('@/views/recruitment/PartnerComplete.vue'),
    meta: {
      title: '파트너 문의 완료 | 엠피니티(MPNITE)',
      description: '파트너 문의가 접수되었습니다. 담당자가 검토 후 연락드리겠습니다.',
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/partnercomplete',
    redirect: { name: 'PartnerInquiryComplete' },
  },
  {
    path: '/partner/inquiry/receipt/check',
    name: 'PartnerInquiryCheck',
    component: () => import('@/views/recruitment/PartnerInquiryCheck.vue'),
    meta: {
      title: '문의 내역 조회 | 엠피니티(MPNITE)',
      description: '파트너 문의 내역을 조회합니다. 접수번호로 진행 상황을 확인하세요.',
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/partner/inquiry/receipt/:publicId([A-Za-z0-9_-]{6,64})',
    name: 'PartnerInquiryReceipt',
    component: () => import('@/views/recruitment/PartnerInquiryReceipt.vue'),
    props: (route) => ({ publicId: String(route.params.publicId) }),
    meta: {
      title: '문의 영수증 | 엠피니티(MPNITE)',
      description: '파트너 문의 영수증입니다. 문의 내용과 접수 정보를 확인하세요.',
      noIndex: true,
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 8) 상품/키트 (공개)
  // ──────────────────────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────────
  // 21) 상품 목록
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/products',
    name: 'products',
    component: () => import('@/views/GoodsList.vue'),
    meta: {
      // ❌ 기존: '3D프린팅 키트·상품'
      // ✅ 수정: "제조 키트·상품"으로 범용화 (3D프린팅은 /3dmanufacturing 전담)
      title: '제조 키트·상품 | 엠피니티(MPNITE)',

      // ❌ 기존: '3D프린터로 제작된 키트...'
      // ✅ 수정
      description: '다양한 제조 방식으로 제작된 키트, 시제품, 완제품 목록을 확인하세요. 교육용 키트부터 실용적인 제품까지 준비되어 있습니다.',

      schemaKind: "productList",
      breadcrumb: bc(...HOME_BC, { name: "상품 목록" }),
      sitemap: { priority: 0.7, changefreq: 'weekly' },
    },
  },
  {
    path: '/goodslist',
    redirect: '/products',
    meta: {
      noIndex: true,
      sitemap: false,
    },
  },

  {
    path: '/kitdetail/:productId',
    name: 'kitdetail',
    component: () => import('@/views/ProductDetail.vue'),
    meta: {
      title: '제품 상세 | 엠피니티(MPNITE)',
      description: '제품 상세 정보를 확인하세요. 스펙, 가격, 리뷰 등 구매에 필요한 정보를 제공합니다.',
      schemaKind: "productDetail",
      breadcrumb: bc(
        ...HOME_BC,
        { name: "상품 목록", url: "/products" },
        { name: "상품 상세" }
      ),
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 9) 팝업 컴포넌트 (sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/cartpopup',
    name: 'cartpopup',
    component: () => import('@/components/popup/CartPopup.vue'),
    meta: {
      title: '장바구니 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/headersettingpopup',
    name: 'headersettingpopup',
    component: () => import('@/components/layouts/HeaderSettingPopup.vue'),
    meta: {
      title: '설정 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/materialdetailpopup',
    name: 'materialdetailpopup',
    component: () => import('@/components/popup/MaterialDetailPopup.vue'),
    meta: {
      title: '소재 상세 정보 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 10) 마이페이지 (로그인 필요 - sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/mypage',
    component: MyPageLayout,
    meta: {
      requiresAuth: true,
      title: '마이페이지 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
    children: [
      { path: '', redirect: 'order' },
      {
        path: 'order',
        name: 'myorder',
        component: () => import('@/views/mypage/MyOrder.vue'),
        meta: {
          requiresAuth: true,
          title: '주문·견적 내역 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'account',
        name: 'myaccount',
        component: () => import('@/views/mypage/MyAccount.vue'),
        meta: {
          requiresAuth: true,
          title: '계정 정보 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'address',
        name: 'myaddress',
        component: () => import('@/views/mypage/MyAddress.vue'),
        meta: {
          requiresAuth: true,
          title: '배송지 관리 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'coupon',
        name: 'mycoupon',
        component: () => import('@/views/mypage/MyCoupon.vue'),
        meta: {
          requiresAuth: true,
          title: '쿠폰·프로모션 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'model',
        name: 'mymodel',
        component: () => import('@/views/mypage/MyModel.vue'),
        meta: {
          requiresAuth: true,
          title: '내 3D 모델 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'idealist',
        name: 'myidealist',
        component: () => import('@/views/mypage/MyIdeaList.vue'),
        meta: {
          requiresAuth: true,
          title: '아이디어 견적 내역 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'ideadetail',
        name: 'MyIdeaDetail',
        component: () => import('@/views/mypage/MyIdeaDetail.vue'),
        meta: {
          requiresAuth: true,
          activeMenu: 'Idea Quote',
          title: '아이디어 견적 상세 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
      {
        path: 'alarm',
        name: 'myalarm',
        component: () => import('@/views/mypage/MyAlarm.vue'),
        meta: {
          requiresAuth: true,
          title: '알림함 | 엠피니티(MPNITE)',
          noIndex: true,
          sitemap: false,
        },
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 11) 약관/정책 (공개)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/useterm',
    name: 'useterm',
    component: () => import('@/components/layouts/useTerm.vue'),
    meta: {
      title: '서비스 이용약관 | 엠피니티(MPNITE)',
      description: '엠피니티 온라인 제조 플랫폼 이용약관입니다. 서비스 이용 시 적용되는 권리, 의무, 금지 사항 및 운영 정책을 확인하세요.',
      breadcrumb: bc(...HOME_BC, { name: "이용약관" }),
      sitemap: { priority: 0.3, changefreq: 'yearly' },
    },
  },
  {
    path: '/privacyterm',
    name: 'privacyterm',
    component: () => import('@/components/layouts/privacyTerm.vue'),
    meta: {
      title: '개인정보 처리방침 | 엠피니티(MPNITE)',
      description: '엠피니티 개인정보 처리방침입니다. 이용자의 개인정보 수집, 이용, 보관, 보호 조치 및 관련 정책을 안내합니다.',
      breadcrumb: bc(...HOME_BC, { name: "개인정보 처리방침" }),
      sitemap: { priority: 0.3, changefreq: 'yearly' },
    },
  },
  {
    path: '/security',
    name: 'security',
    component: () => import('@/components/layouts/SecuritySystem.vue'),
    meta: {
      title: '보안·인증 체계 안내 | 엠피니티(MPNITE)',
      description: '엠피니티의 보안·인증 체계를 안내합니다. 데이터 보호, 암호화, 인증 절차, 접근 제어 등 플랫폼 보안 정책을 확인하세요.',
      breadcrumb: bc(...HOME_BC, { name: "보안·인증" }),
      sitemap: { priority: 0.3, changefreq: 'yearly' },
    },
  },
  {
    path: '/platformagreement',
    name: 'platformagreement',
    component: () => import('@/components/layouts/PlatformAgreement.vue'),
    meta: {
      title: '플랫폼 이용 계약 | 엠피니티(MPNITE)',
      description: '엠피니티 플랫폼 이용 계약 안내입니다. 파트너사 및 이용자의 계약 조건과 권리, 의무 사항을 확인하세요.',
      breadcrumb: bc(...HOME_BC, { name: "플랫폼 이용 계약" }),
      sitemap: { priority: 0.3, changefreq: 'yearly' },
    },
  },
  {
    path: '/support-policy',
    name: 'support-policy',
    component: () => import('@/components/layouts/SupportPolicy.vue'),
    meta: {
      title: '브라우저 지원 정책 | 엠피니티(MPNITE)',
      description: '엠피니티의 브라우저 및 환경 지원 정책입니다. 서비스 이용을 위한 권장 브라우저, 지원 운영체제, 호환성 정보를 확인하세요.',
      breadcrumb: bc(...HOME_BC, { name: "지원 정책" }),
      sitemap: { priority: 0.3, changefreq: 'yearly' },
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 12) 게시판 (공개)
  // ──────────────────────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────────
  // 22) 공지사항
  // ──────────────────────────────────────────────────────────────────
  {
    path: '/board',
    name: 'BoardPostList',
    component: () => import('@/views/board/BoardPostList.vue'),
    meta: {
      title: '공지사항·소식 | 엠피니티(MPNITE)',

      // ❌ 기존: '엠피니티 공지사항, 3D프린팅 업계 소식...'
      // ✅ 수정: "엠피니티" 선두 제거, "3D프린팅" 제거
      description: '공지사항, 업계 소식, 이벤트 안내를 확인하세요. 서비스 업데이트와 프로모션 정보를 놓치지 마세요.',

      schemaKind: "boardList",
      breadcrumb: bc(...HOME_BC, { name: "공지사항" }),
      sitemap: { priority: 0.6, changefreq: 'daily' },
    },
  },
  {
    path: '/board/new',
    name: 'BoardPostCreate',
    component: () => import('@/views/board/BoardPostCreate.vue'),
    meta: {
      requiresAuth: true,
      title: '게시글 작성 | 엠피니티(MPNITE)',
      breadcrumb: bc(
        ...HOME_BC,
        { name: "공지사항·게시판", url: "/board" },
        { name: "게시글 작성" }
      ),
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/board/:id',
    name: 'BoardPostDetailView',
    component: () => import('@/views/board/BoardPostDetail.vue'),
    meta: {
      title: '게시글 상세 | 엠피니티(MPNITE)',
      description: '엠피니티 게시판 게시글 상세 내용입니다.',
      schemaKind: "boardDetail",
      breadcrumb: bc(
        ...HOME_BC,
        { name: "공지사항·게시판", url: "/board" },
        { name: "게시글 상세" }
      ),
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 13) 관리자 전용 (sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/admin',
    name: 'adminDashboard',
    component: () => import('@/views/admin/AdminDashboard.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: [ROLES.ADMIN],
      title: '관리자 대시보드 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },
  {
    path: '/admin/users',
    name: 'adminUsers',
    component: () => import('@/views/admin/AdminUsers.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: [ROLES.ADMIN],
      title: '회원 관리 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 14) 개발/도구 (sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/initreset',
    name: 'initreset',
    component: () => import('@/components/common/InitReset.vue'),
    meta: {
      title: '환경 초기화 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 15) 에러 페이지 (sitemap 제외)
  // ──────────────────────────────────────────────────────────────────────────
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/error/ForbiddenView.vue'),
    meta: {
      title: '접근 권한 없음 | 엠피니티(MPNITE)',
      description: '접근 권한이 없는 페이지입니다. 로그인하시거나 관리자에게 문의해주세요.',
      noIndex: true,
      sitemap: false,
    },
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: {
      title: '페이지를 찾을 수 없음 | 엠피니티(MPNITE)',
      description: '요청하신 페이지를 찾을 수 없습니다. 주소를 확인하시거나 홈으로 이동해주세요.',
      noIndex: true,
      sitemap: false,
    },
  },

  {
    path: '/downLoadFile',
    name: 'downLoadFile',
    component: () => import('@/components/common/DownLoadFile.vue'),
    meta: {
      title: '사업자 등록증 | 엠피니티(MPNITE)',
      noIndex: true,
      sitemap: false,
    },
  },
];